import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, useLanguage, API } from "@/App";
import {
  Home, Briefcase, Building, Car, Inbox, MessageCircle, Plane, GraduationCap, Map, Headphones,
  ShoppingBag, Users, BookOpen, Heart, Video, Bot, User, Shield, FileText,
  LogOut, Menu, X, Globe, Sun, Moon, Sparkles, Search, Bell, Crown, Eye, Play,
} from "lucide-react";
import { useEffect, useState } from "react";
import { ARAB_SANAD_LOGO, ARAB_SANAD_LOGO_FALLBACK, NY_SUNSET_BG, NY_SUNSET_BG_FALLBACK, NAV_THUMBS, PROMO_MOUNTAINS, ROBOT_ASSISTANT } from "@/lib/brand";
import { applyTheme, getTheme } from "@/lib/theme";
import { Button } from "@/components/ui/button";
import BottomNav from "@/components/BottomNav";
import NotificationPrompt from "@/components/NotificationPrompt";

const getNavItems = (t, role) => {
  const items = [
    { path: "/dashboard",      icon: Home,          label: t("الرئيسية", "Home") },
    { path: "/ai-assistant",   icon: Bot,           label: t("المساعد الذكي", "AI Assistant") },
    { path: "/jobs",           icon: Briefcase,     label: t("الوظائف", "Jobs") },
    { path: "/housing",        icon: Building,      label: t("السكن", "Housing") },
    { path: "/vehicles",       icon: Car,           label: t("السيارات", "Vehicles") },
    { path: "/community",      icon: Users,         label: t("المجتمع", "Community") },
    { path: "/tv",             icon: Video,         label: t("Arab Sanad TV", "Arab Sanad TV") },
    { path: "/education",      icon: GraduationCap, label: t("التعليم", "Education") },
    { path: "/services",       icon: Users,         label: t("الخدمات", "Services") },
    { path: "/discover",       icon: Map,           label: t("اكتشف أمريكا", "Discover America") },
    { path: "/airport-pickup", icon: Plane,         label: t("استقبال المطار", "Airport Pickup") },
    { path: "/messages",       icon: MessageCircle, label: t("الرسائل", "Messages") },
    { path: "/chat/inbox",     icon: Inbox,         label: t("صندوق الرسائل", "Inbox") },
    { path: "/support",        icon: Headphones,    label: t("الدعم الفني", "Support Chat") },
    { path: "/cv",             icon: FileText,      label: t("مركز السيرة الذاتية", "Resume Center") },
    { path: "/favorites",      icon: Heart,         label: t("المفضلة", "Favorites") },
    { path: "/guide",          icon: BookOpen,      label: t("دليل المهاجر", "Immigrant Guide") },
    { path: "/grocery/coming-soon", icon: ShoppingBag, label: t("سوق البقالة", "Grocery"), badge: t("قريباً", "Soon") },
    { path: "/profile",        icon: User,          label: t("الملف الشخصي", "Profile") },
  ];
  if (["admin", "owner", "support", "viewer", "video_contributor"].includes(role)) {
    items.push({ path: "/admin", icon: Shield, label: t("لوحة الإدارة", "Admin") });
  }
  return items;
};

// ─── Theme toggle (3-state: Light / Dark / System) ───
function ThemeToggle() {
  const { t } = useLanguage();
  const [theme, setTheme] = useState(getTheme());
  useEffect(() => {
    const onChange = () => setTheme(getTheme());
    window.addEventListener("themechange", onChange);
    return () => window.removeEventListener("themechange", onChange);
  }, []);
  const options = [
    { v: "light",  Icon: Sun,       label_ar: "فاتح", label_en: "Light"  },
    { v: "auto",   Icon: Sparkles,  label_ar: "تلقائي", label_en: "Auto" },
    { v: "dark",   Icon: Moon,      label_ar: "داكن", label_en: "Dark"   },
  ];
  return (
    <div className="px-2 py-1.5 rounded-2xl grid grid-cols-3 gap-1" style={{ background: "var(--bg-cream)" }} data-testid="theme-toggle-group">
      {options.map((o) => {
        const active = theme === o.v;
        return (
          <button key={o.v} onClick={() => { applyTheme(o.v); setTheme(o.v); }}
            className="flex flex-col items-center gap-0.5 py-1.5 rounded-xl transition-all"
            style={{
              background: active ? "var(--accent-gold)" : "transparent",
              color: active ? "#fff" : "var(--text-secondary)",
              boxShadow: active ? "var(--shadow-gold)" : "none",
            }}
            data-testid={`theme-${o.v}`}
            title={t(o.label_ar, o.label_en)}>
            <o.Icon size={14} />
            <span className="text-[10px] font-bold">{t(o.label_ar, o.label_en)}</span>
          </button>
        );
      })}
    </div>
  );
}

// ─── Sidebar layout shell ───
export const DashboardLayout = ({ children, title }) => {
  const { user, logout } = useAuth();
  const { t, toggleLanguage, language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navItems = getNavItems(t, user?.role);
  const handleLogout = async () => { await logout(); navigate("/"); };

  const roleBadge = user?.role && !["user"].includes(user.role) ? user.role : null;

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-base)" }}>
      {/* Mobile Header */}
      <header className="lg:hidden h-16 flex items-center px-4 fixed top-0 left-0 right-0 z-50 glass border-b" style={{ borderColor: "var(--border-soft)" }}>
        <button onClick={() => setSidebarOpen(true)} className="p-2" style={{ color: "var(--text-primary)" }} data-testid="mobile-menu-toggle">
          <Menu size={22} />
        </button>
        <img
          src={ARAB_SANAD_LOGO}
          alt="Arab Sanad"
          className="h-9 mx-auto"
          onError={(e) => { if (e.target.src !== ARAB_SANAD_LOGO_FALLBACK) e.target.src = ARAB_SANAD_LOGO_FALLBACK; }}
        />
        <div className="flex items-center gap-2">
          <Link
            to="/profile"
            className="w-9 h-9 rounded-full overflow-hidden border-2"
            style={{ borderColor: "var(--accent-gold)" }}
            aria-label={t("الملف الشخصي", "Profile")}
            data-testid="mobile-profile-avatar"
          >
            {user?.avatar ? <img src={user.avatar.startsWith("http") ? user.avatar : `${API}/uploads/${user.avatar}`} alt="" className="w-full h-full object-cover" />
              : <div className="w-full h-full grid place-items-center text-white font-bold" style={{ background: "var(--accent-gold)" }}>{user?.name?.charAt(0) || "س"}</div>}
          </Link>
          {/* Mobile-only quick logout — desktop sidebar already has its own logout */}
          <button
            onClick={handleLogout}
            className="p-2 rounded-full hover:bg-red-500/10 transition-colors"
            style={{ color: "#FCA5A5" }}
            aria-label={t("تسجيل خروج", "Logout")}
            title={t("تسجيل خروج", "Logout")}
            data-testid="mobile-logout-btn"
          >
            <LogOut size={20} />
          </button>
        </div>
      </header>

      {sidebarOpen && (<div className="lg:hidden fixed inset-0 bg-black/55 z-40 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />)}

      {/* Sidebar */}
      <aside className={`sidebar fixed top-0 right-0 h-full w-72 z-50 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}`} data-testid="sidebar">
        <div className="px-4 py-5 h-full flex flex-col">
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden absolute top-4 left-4" style={{ color: "var(--text-primary)" }}><X size={22} /></button>

          {/* Logo header */}
          <Link to="/dashboard" className="flex items-center gap-2.5 mb-5 px-2">
            <img
              src={ARAB_SANAD_LOGO}
              alt="Arab Sanad"
              className="h-12 w-auto"
              onError={(e) => { if (e.target.src !== ARAB_SANAD_LOGO_FALLBACK) e.target.src = ARAB_SANAD_LOGO_FALLBACK; }}
            />
            <div>
              <div className="font-['Cairo'] font-black text-lg" style={{ color: "var(--text-primary)" }}>Arab Sanad</div>
              <div className="text-[11px] font-bold" style={{ color: "var(--accent-gold)" }}>عرب سند</div>
            </div>
          </Link>

          {/* User card */}
          <div className="card-soft p-3 mb-4" style={{ background: "var(--bg-surface)" }}>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full overflow-hidden flex-shrink-0 border-2" style={{ borderColor: "var(--accent-gold)" }}>
                {user?.avatar ? <img src={user.avatar.startsWith("http") ? user.avatar : `${API}/uploads/${user.avatar}`} alt="" className="w-full h-full object-cover" /> :
                  <div className="w-full h-full grid place-items-center text-white font-bold" style={{ background: "var(--accent-gold)" }}>{user?.name?.charAt(0) || "س"}</div>}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm truncate" style={{ color: "var(--text-primary)" }}>{user?.name || t("مستخدم", "User")}</p>
                <p className="text-[11px] truncate" style={{ color: "var(--text-secondary)" }}>{user?.email}</p>
              </div>
            </div>
            {roleBadge && (
              <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
                   style={{ background: "var(--accent-gold-soft)", color: "var(--accent-gold-hover)" }}>
                <Crown size={10} /> {roleBadge}
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto pr-1 -mr-1 space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path || (item.path !== "/dashboard" && location.pathname.startsWith(item.path + "/"));
              return (
                <Link key={item.path} to={item.path} onClick={() => setSidebarOpen(false)}
                      className={`nav-item ${isActive ? "nav-item-active" : ""}`}
                      data-testid={`nav-${item.path.slice(1).replace(/\//g, '-')}`}>
                  <span className="nav-label flex items-center gap-2">
                    {item.label}
                    {item.badge && (
                      <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide bg-yellow-500/20 text-yellow-300 border border-yellow-500/30">
                        {item.badge}
                      </span>
                    )}
                  </span>
                  <item.icon className="nav-icon" />
                </Link>
              );
            })}
          </nav>

          {/* Footer: lang + logout (theme toggle removed — dark only) */}
          <div className="mt-3 pt-3 border-t space-y-1" style={{ borderColor: "var(--border-soft)" }}>
            <button onClick={toggleLanguage} className="nav-item w-full" style={{ color: "var(--text-primary)" }} data-testid="language-toggle">
              <span className="nav-label">{language === "ar" ? "English" : "العربية"}</span>
              <Globe className="nav-icon" />
            </button>
            <button onClick={handleLogout} className="nav-item w-full" style={{ color: "#FCA5A5" }} data-testid="logout-btn">
              <span className="nav-label">{t("تسجيل خروج", "Logout")}</span>
              <LogOut className="nav-icon" style={{ color: "#FCA5A5" }} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:mr-72 min-h-screen pt-14 lg:pt-0 pb-20 lg:pb-0">
        <div className="px-3 sm:px-6 lg:px-8 py-4 lg:py-6">
          {title && (
            <div className="mb-4 lg:mb-6 flex items-center justify-between">
              <h1 className="font-['Cairo'] text-xl sm:text-2xl lg:text-3xl font-black" style={{ color: "var(--text-primary)" }}>{title}</h1>
            </div>
          )}
          {children}
        </div>
      </main>
      <BottomNav />
    </div>
  );
};

// ─── Dashboard page content ───
export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const categories = [
    { path: "/jobs",      label: t("الوظائف", "Jobs"),         sub: t("فرص عمل مميزة", "Premium jobs"),       img: NAV_THUMBS["/jobs"] },
    { path: "/housing",   label: t("السكن", "Housing"),        sub: t("خيارات سكن متنوعة", "Diverse homes"),   img: NAV_THUMBS["/housing"] },
    { path: "/vehicles",  label: t("السيارات", "Vehicles"),    sub: t("شراء وبيع السيارات", "Buy & sell cars"), img: NAV_THUMBS["/vehicles"] },
    { path: "/community", label: t("المجتمع", "Community"),    sub: t("تواصل وشارك", "Connect & share"),        img: NAV_THUMBS["/community"] },
    { path: "/tv",        label: t("Arab Sanad TV", "Arab Sanad TV"), sub: t("برامج حصرية", "Exclusive shows"), img: NAV_THUMBS["/tv"] },
    { path: "/discover",  label: t("اكتشف أمريكا", "Discover America"), sub: t("أماكن وتجارب رائعة", "Amazing places"), img: NAV_THUMBS["/discover"] },
  ];

  const trustBadges = [
    { icon: Shield, label_ar: "موثوق وآمن",  label_en: "Trusted & Secure",  desc_ar: "حماية وخصوصية بمعايير عالمية", desc_en: "World-class privacy" },
    { icon: Users,  label_ar: "مجتمعك معك",  label_en: "Community by you", desc_ar: "تواصل مع آلاف العرب في أمريكا", desc_en: "Thousands of Arabs" },
    { icon: Sparkles, label_ar: "كل ما تحتاجه", label_en: "All-in-one",    desc_ar: "خدمات متكاملة في مكان واحد", desc_en: "Everything in one" },
    { icon: Heart,  label_ar: "دعم دائم",    label_en: "Always-on support", desc_ar: "فريق عربي 24/7", desc_en: "24/7 Arabic team" },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-4 lg:space-y-6 max-w-7xl mx-auto" data-testid="dashboard-content">
        <NotificationPrompt />
        {/* Welcome strip */}
        <div className="flex items-center justify-between gap-3 -mt-2 mb-2">
          <div className="flex items-center gap-3 flex-1 max-w-xl card-soft px-4 py-2.5" style={{ background: "var(--bg-surface)" }}>
            <Search size={16} style={{ color: "var(--text-muted)" }} />
            <input placeholder={t("ابحث عن أي شيء…", "Search anything…")} className="flex-1 bg-transparent border-0 outline-0 text-sm" data-testid="global-search" />
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/messages")} className="relative w-10 h-10 rounded-full grid place-items-center card-soft" style={{ background: "var(--bg-surface)" }} data-testid="messages-btn">
              <MessageCircle size={16} style={{ color: "var(--text-primary)" }} />
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full text-[10px] font-bold text-white" style={{ background: "var(--accent-gold)" }}>2</span>
            </button>
            <button className="relative w-10 h-10 rounded-full grid place-items-center card-soft" style={{ background: "var(--bg-surface)" }} data-testid="notifications-btn">
              <Bell size={16} style={{ color: "var(--text-primary)" }} />
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full text-[10px] font-bold text-white" style={{ background: "var(--accent-gold)" }}>3</span>
            </button>
            <button onClick={() => navigate("/profile")} className="flex items-center gap-2 px-3 py-1.5 rounded-full card-soft" style={{ background: "var(--bg-surface)" }}>
              <span className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>{user?.name?.split(" ")[0] || t("ضيف", "Guest")}</span>
              <div className="w-9 h-9 rounded-full overflow-hidden border-2" style={{ borderColor: "var(--accent-gold)" }}>
                {user?.avatar ? <img src={user.avatar.startsWith("http") ? user.avatar : `${API}/uploads/${user.avatar}`} alt="" className="w-full h-full object-cover" /> :
                  <div className="w-full h-full grid place-items-center text-white font-bold text-sm" style={{ background: "var(--accent-gold)" }}>{user?.name?.charAt(0) || "س"}</div>}
              </div>
            </button>
          </div>
        </div>

        {/* Hero */}
        <div className="relative rounded-[28px] overflow-hidden card-soft" data-testid="dashboard-hero">
          <img
            src={NY_SUNSET_BG}
            alt="New York skyline at sunset with the Statue of Liberty"
            className="absolute inset-0 w-full h-full object-cover object-center"
            loading="eager"
            onError={(e) => { if (e.target.src !== NY_SUNSET_BG_FALLBACK) e.target.src = NY_SUNSET_BG_FALLBACK; }}
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(270deg, rgba(15, 27, 51, 0.62) 0%, rgba(15, 27, 51, 0.38) 50%, rgba(15, 27, 51, 0.18) 100%)" }} />
          <div className="relative z-10 p-6 sm:p-8 md:p-12 min-h-[200px] sm:min-h-[280px] md:min-h-[340px] flex flex-col justify-center text-white">
            <h2 className="font-['Cairo'] font-black text-2xl sm:text-3xl md:text-5xl mb-2 sm:mb-3 drop-shadow leading-tight" style={{ color: "#FFFFFF" }}>
              {t("مرحباً بك،", "Welcome,")} <span style={{ color: "#FFFFFF" }}>{user?.name?.split(" ")[0] || t("صديقي", "friend")}</span>
            </h2>
            <p className="text-sm sm:text-base md:text-lg drop-shadow mb-3 sm:mb-5 leading-snug" style={{ color: "#E2E8F0" }}>
              {t("منصتك المتكاملة للحياة في الولايات المتحدة", "Your all-in-one platform for life in the United States")}
            </p>
            <div className="flex flex-wrap gap-2 sm:gap-2.5">
              <button onClick={() => navigate("/profile")} className="btn-primary text-sm" data-testid="hero-profile-btn">
                <User size={14} className="inline ml-1" />{t("الملف الشخصي", "My Profile")}
              </button>
            </div>
          </div>
        </div>

        {/* Category grid */}
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-3" data-testid="category-grid">
          {categories.map((c) => (
            <button key={c.path} onClick={() => navigate(c.path)}
              className="card-soft p-0 overflow-hidden text-right group" style={{ background: "var(--bg-surface)" }}
              data-testid={`cat-${c.path.slice(1)}`}>
              <div className="relative h-20 sm:h-24 lg:h-28 overflow-hidden">
                <img src={c.img} alt={c.label}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                  onError={(e) => { if (e.target.src !== NY_SUNSET_BG_FALLBACK) e.target.src = NY_SUNSET_BG_FALLBACK; }} />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.7), transparent 60%)" }} />
                <div className="absolute bottom-1.5 right-2 text-white drop-shadow font-['Cairo'] font-bold text-xs sm:text-sm">{c.label}</div>
              </div>
              <div className="p-1.5 sm:p-2 text-[10px] sm:text-xs leading-tight" style={{ color: "var(--text-secondary)" }}>{c.sub}</div>
            </button>
          ))}
        </div>

        {/* 3 panels */}
        <div className="grid lg:grid-cols-3 gap-4">
          {/* AI assistant card — featured robot + suggested prompts */}
          <div className="card-soft p-5 relative overflow-hidden" data-testid="ai-card" style={{ background: "var(--bg-surface)" }}>
            <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: "var(--accent-gold-soft)", color: "var(--accent-gold)" }}>{t("جديد", "New")}</div>
            <h4 className="font-['Cairo'] font-black text-lg flex items-center gap-2 mb-3" style={{ color: "var(--text-primary)" }}>
              <Sparkles size={18} style={{ color: "var(--accent-gold)" }} /> {t("المساعد الذكي", "AI Assistant")}
            </h4>
            <div className="flex flex-col items-center text-center mb-3">
              <div className="relative w-24 h-24 mb-2 rounded-full grid place-items-center"
                   style={{ background: "radial-gradient(circle, var(--accent-gold-soft), transparent 70%)" }}>
                <img src={ROBOT_ASSISTANT} alt="Arab Sanad AI" className="w-20 h-20 object-contain drop-shadow-lg"
                     onError={(e) => { e.target.style.display = 'none'; const fb = e.target.nextSibling; if (fb) fb.style.display = 'grid'; }} />
                <div className="absolute inset-1 rounded-full grid place-items-center" style={{ background: "linear-gradient(135deg, var(--accent-gold), var(--accent-gold-hover))", display: 'none' }}>
                  <Bot size={42} className="text-white" />
                </div>
              </div>
              <p className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>{t("مرحباً، أنا هنا لمساعدتك", "Hi, I'm here to help")}</p>
              <p className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>{t("اسألني عن أي خدمة تحتاجها", "Ask me about any service")}</p>
            </div>
            {/* Suggested prompts */}
            <div className="space-y-1.5 mb-3" data-testid="ai-suggested-prompts">
              {[
                { ar: "ابحث عن وظيفة مناسبة",       en: "Find a suitable job" },
                { ar: "اقترح عليّ سكن في نيويورك",  en: "Suggest housing in NYC" },
                { ar: "ساعدني في تجهيز السيرة الذاتية", en: "Help me with my resume" },
              ].map((p, i) => (
                <button key={i} onClick={() => navigate(`/ai-assistant?q=${encodeURIComponent(t(p.ar, p.en))}`)}
                  className="w-full text-right px-3 py-2 rounded-xl text-xs transition-colors hover:shadow"
                  style={{ background: "var(--bg-cream)", color: "var(--text-primary)", border: "1px solid var(--border-soft)" }}
                  data-testid={`ai-prompt-${i}`}>
                  💡 {t(p.ar, p.en)}
                </button>
              ))}
            </div>
            <button onClick={() => navigate("/ai-assistant")} className="btn-primary w-full text-sm" data-testid="ai-cta">
              <Sparkles size={14} className="inline ml-1" /> {t("ابدأ محادثة جديدة", "Start a new chat")}
            </button>
          </div>

          {/* Featured content */}
          <div className="card-soft p-5" data-testid="featured-card" style={{ background: "var(--bg-surface)" }}>
            <h4 className="font-['Cairo'] font-black text-lg flex items-center gap-2 mb-3" style={{ color: "var(--text-primary)" }}>
              <Play size={18} style={{ color: "var(--accent-gold)" }} /> {t("محتوى مميز", "Featured content")}
            </h4>
            <div className="space-y-2.5">
              {[
                { title: t("كيف تجد عمل في أمريكا", "Find a job in America"), time: "18:45" },
                { title: t("أفضل المدن للعيش", "Best cities to live"), time: "22:05" },
                { title: t("5 خطوات للحصول على إقامة دائمة", "5 steps to permanent residency"), time: "16:30" },
              ].map((v, i) => (
                <div key={i} onClick={() => navigate("/tv")} className="flex items-center gap-3 p-2 rounded-xl hover:bg-cream cursor-pointer transition-colors" style={{ background: "var(--bg-cream)" }}>
                  <div className="w-16 h-10 rounded-md bg-cover bg-center flex-shrink-0 relative" style={{ backgroundImage: `url(${NAV_THUMBS["/tv"]})` }}>
                    <Play size={14} className="absolute inset-0 m-auto text-white drop-shadow" fill="white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold truncate" style={{ color: "var(--text-primary)" }}>{v.title}</div>
                    <div className="text-[10px]" style={{ color: "var(--text-muted)" }}>{v.time}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Community activity */}
          <div className="card-soft p-5" data-testid="activity-card" style={{ background: "var(--bg-surface)" }}>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-['Cairo'] font-black text-lg flex items-center gap-2" style={{ color: "var(--text-primary)" }}>
                <Users size={18} style={{ color: "var(--accent-gold)" }} /> {t("نشاط المجتمع", "Community activity")}
              </h4>
              <button onClick={() => navigate("/community")} className="text-xs font-bold" style={{ color: "var(--accent-gold-hover)" }}>{t("عرض الكل", "View all")}</button>
            </div>
            <div className="space-y-2.5">
              {[
                { name: t("أحمد محمد", "Ahmed M."), action: t("منشور جديد في مجموعة نيويورك", "new post in NY group"), time: "5m" },
                { name: t("سارة علي", "Sara A."),   action: t("انضمت إلى مجموعة ريادة الأعمال", "joined Entrepreneurs"),  time: "15m" },
                { name: t("فريق التسويق", "Mktg"),  action: t("3 وظائف جديدة في قسم التسويق", "3 new jobs in marketing"), time: "20m" },
              ].map((a, i) => (
                <div key={i} className="flex items-center gap-2.5 p-2 rounded-xl" style={{ background: "var(--bg-cream)" }}>
                  <div className="w-9 h-9 rounded-full grid place-items-center text-white font-bold text-sm flex-shrink-0" style={{ background: "var(--accent-gold)" }}>{a.name.charAt(0)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold truncate" style={{ color: "var(--text-primary)" }}>{a.name}</div>
                    <div className="text-[11px] truncate" style={{ color: "var(--text-secondary)" }}>{a.action}</div>
                  </div>
                  <span className="text-[10px]" style={{ color: "var(--text-muted)" }}>{a.time}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Trust strip */}
        <div className="card-soft p-5 grid grid-cols-2 md:grid-cols-4 gap-4" style={{ background: "var(--bg-surface)" }} data-testid="trust-strip">
          {trustBadges.map((b, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl grid place-items-center flex-shrink-0" style={{ background: "var(--accent-gold-soft)" }}>
                <b.icon size={20} style={{ color: "var(--accent-gold)" }} />
              </div>
              <div>
                <div className="font-['Cairo'] font-bold text-sm" style={{ color: "var(--text-primary)" }}>{t(b.label_ar, b.label_en)}</div>
                <div className="text-[11px]" style={{ color: "var(--text-secondary)" }}>{t(b.desc_ar, b.desc_en)}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Promo card — Discover America CTA (the only Discover America CTA on the page) */}
        <div className="relative rounded-[28px] overflow-hidden card-soft min-h-[200px]" data-testid="promo-card">
          <img
            src="/discover-america-banner.jpg"
            alt={t("اكتشف أمريكا", "Discover America")}
            className="absolute inset-0 w-full h-full object-cover object-center"
            loading="lazy"
            onError={(e) => { if (e.target.src !== PROMO_MOUNTAINS) e.target.src = PROMO_MOUNTAINS; }}
          />
          {/* Dark overlay for white-text readability — same card style preserved */}
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(10, 18, 38, 0.78) 0%, rgba(10, 18, 38, 0.5) 55%, rgba(10, 18, 38, 0.25) 100%)" }} />
          <div className="relative z-10 p-8 md:p-10 text-white">
            <h3 className="font-['Cairo'] font-black text-2xl md:text-3xl mb-2 drop-shadow" style={{ color: "#FFFFFF" }}>
              {t("اكتشف أمريكا", "Discover America")}
            </h3>
            <p className="mb-4 drop-shadow max-w-md text-sm md:text-base" style={{ color: "#FFFFFF", opacity: 0.95 }}>
              {t("مدن، سياحة، طبيعة وتجارب جديدة في جميع أنحاء الولايات المتحدة.",
                 "Cities, tourism, nature and new experiences across the United States.")}
            </p>
            <button onClick={() => navigate("/discover")} className="btn-primary text-sm md:text-base" data-testid="promo-cta">
              <Eye size={14} className="inline ml-1.5" /> {t("استكشف الآن", "Explore now")}
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
